import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth state enum
const AuthState = {
  LOADING: 'loading',
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated'
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState(AuthState.LOADING);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken('cookie-auth');
        setAuthState(AuthState.AUTHENTICATED);
        return userData;
      } else {
        setUser(null);
        setToken(null);
        setAuthState(AuthState.UNAUTHENTICATED);
        return null;
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
      setToken(null);
      setAuthState(AuthState.UNAUTHENTICATED);
      return null;
    } finally {
      setIsInitialized(true);
    }
  }, []);

  // Initialize auth on mount
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const refreshUser = useCallback(async () => {
    setAuthState(AuthState.LOADING);
    return await fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = useCallback(async (userData) => {
    // Called after successful OAuth callback
    setUser(userData);
    setToken('cookie-auth');
    setAuthState(AuthState.AUTHENTICATED);
    setIsInitialized(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      setAuthState(AuthState.UNAUTHENTICATED);
    }
  }, []);

  // Helper functions for role checking
  const hasRole = useCallback((role) => {
    if (!user) return false;
    return user.roles?.includes(role) || user.system_role === role;
  }, [user]);

  const isSuperAdmin = useCallback(() => {
    return hasRole('super_admin');
  }, [hasRole]);

  const isAdmin = useCallback(() => {
    return hasRole('admin') || hasRole('super_admin');
  }, [hasRole]);

  const value = {
    // State
    user,
    token,
    authState,
    isInitialized,
    
    // Computed
    isLoading: authState === AuthState.LOADING,
    isAuthenticated: authState === AuthState.AUTHENTICATED,
    
    // Actions
    login,
    logout,
    refreshUser,
    fetchCurrentUser,
    
    // Role helpers
    hasRole,
    isSuperAdmin,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthState };
export default AuthContext;
